import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Mail } from "lucide-react";
import { SupportChat } from "./SupportChat";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { CONTACT, waHref } from "@/data/contact";

const KEY = "gd-wa-region";
const detectRegion = () => {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "IN" || saved === "US") return saved;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const lang = (navigator.language || "").toUpperCase();
    return tz === "Asia/Kolkata" || tz === "Asia/Calcutta" || lang.endsWith("-IN") ? "IN" : "US";
  } catch { return "US"; }
};
const TEXT = encodeURIComponent("Hello GEO DRAFTING, I have a survey CAD drafting enquiry.");

export const WhatsAppButton = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState("US");
  const [tab, setTab] = useState("wa");
  useEffect(() => { setRegion(detectRegion()); }, []);
  useEffect(() => { setOpen(false); }, [pathname]);
  if (pathname.startsWith("/admin")) return null;
  const pick = (r) => { setRegion(r); try { localStorage.setItem(KEY, r); } catch { /* private mode */ } };

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3 sm:bottom-7 sm:right-7" data-testid="whatsapp-widget">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.96 }} transition={{ duration: 0.22 }}
            className="w-[320px] border border-white/10 bg-[#081828] text-white shadow-[0_30px_80px_-20px_rgba(0,0,0,.8)]" data-testid="whatsapp-popover" role="dialog" aria-label="Customer support — WhatsApp or email">
            <span aria-hidden className="block h-[2px] bg-gradient-to-r from-[#25D366] via-[#D8B66A]/60 to-transparent" />
            <div className="flex items-center justify-between px-5 pt-4">
              <p className="eyebrow text-[10px] text-[#D8B66A]">Customer support</p>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="whatsapp-close" className="grid h-7 w-7 place-items-center text-[#9fb3c8] hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 grid grid-cols-2 border-b border-white/10" role="tablist">
              {[["wa", "WhatsApp chat", WhatsAppIcon], ["email", "Email chat", Mail]].map(([id, label, Icon]) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`support-tab-${id}`}
                  className={`relative flex items-center justify-center gap-2 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${tab === id ? "text-white" : "text-[#9fb3c8] hover:text-white"}`}>
                  <Icon className={`h-3.5 w-3.5 ${id === "wa" ? "text-[#25D366]" : "text-[#5AA9E6]"}`} />{label}
                  {tab === id && <span className="absolute inset-x-0 -bottom-px h-[2px] bg-[#D8B66A]" />}
                </button>
              ))}
            </div>
            {tab === "email" ? <SupportChat /> : (<>
            <p className="px-5 pt-4 text-[13px] text-[#c3d0dd]">Choose the studio closest to you. Hours: Mon–Fri 8–6, Sat 9–2 EST.</p>
            <ul className="mt-4 divide-y divide-white/[.07] border-t border-white/10">
              {CONTACT.whatsapp.map((n) => (
                <li key={n.region}>
                  <a href={`${waHref(n)}?text=${TEXT}`} target="_blank" rel="noreferrer" onClick={() => pick(n.region)} data-testid={`whatsapp-link-${n.region.toLowerCase()}`}
                    className={`flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-white/[.04] ${region === n.region ? "bg-white/[.03]" : ""}`}>
                    <span className="grid h-9 w-9 shrink-0 place-items-center border border-[#25D366]/40 text-[#25D366]"><WhatsAppIcon className="h-4 w-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9fb3c8]">{n.region === "IN" ? "India studio" : "USA studio"}</span>
                      <span className="block font-mono text-[14px] text-white">{n.label}</span>
                    </span>
                    {region === n.region && <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#D8B66A]" data-testid="whatsapp-suggested">Suggested</span>}
                  </a>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => setTab("email")} className="u-link block w-full border-t border-white/10 px-5 py-3 text-left font-mono text-[10px] uppercase tracking-[0.16em] text-[#9fb3c8] hover:text-white" data-testid="support-switch-email">Prefer email? Send a message instead →</button>
            </>)}
          </motion.div>
        )}
      </AnimatePresence>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Chat with GEO DRAFTING on WhatsApp" data-testid="whatsapp-toggle"
        className="group relative grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-[#07301b] shadow-[0_18px_40px_-12px_rgba(37,211,102,.7)] transition-transform duration-300 hover:scale-105">
        <span aria-hidden className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping [animation-duration:2.4s] group-hover:hidden" />
        <WhatsAppIcon className="relative h-8 w-8" />
        <span className="absolute -left-1 -top-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-[#0A1D30] px-1 font-mono text-[9px] font-bold text-[#D8B66A]" data-testid="whatsapp-region-badge">{region}</span>
      </button>
    </div>
  );
};
